import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Logo } from '@/components/brand/logo';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button, ErrorState, Skeleton, useToast } from '@/components/ui';
import { setupApi } from '../onboarding/setup-api';
import { assistantApi } from './assistant-api';
import { ChatView } from './chat-view';

/* ---------------------------------------------------------------------------
   הקמת העסק בשיחה — ההמשך הישיר של ההרשמה. אותו עוזר, במצב ONBOARDING:
   מסמכים קודם (העוזר מזהה מהם תחום ופרטי עסק), צוות, חיבורים — הכל
   בכרטיסים בתוך השיחה — וסיום דרך אותה לוגיקה כמו האשף (finish).
   --------------------------------------------------------------------------- */

export function AssistantSetup() {
  const { t } = useTranslation('assistant');
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // השרת מחזיר את שיחת ההקמה הקיימת, או פותח אחת עם הודעת פתיחה.
  const conversation = useQuery({
    queryKey: ['assistant', 'onboarding-conversation'],
    queryFn: () => assistantApi.create('ONBOARDING'),
    staleTime: Infinity,
    retry: false,
  });

  const skip = useMutation({
    mutationFn: setupApi.finish,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['onboarding-import', 'status'] });
      void navigate({ to: '/', replace: true });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex h-dvh flex-col bg-canvas">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between px-4 py-3">
        <Logo />
        <div className="flex items-center gap-2">
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col px-4 pb-4">
        <h1 className="text-lg font-semibold text-fg">{t('setup.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('setup.subtitle')}</p>

        <section className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-(--radius-lg) border border-border bg-surface">
          {conversation.isPending ? (
            <Skeleton className="m-3 h-24" />
          ) : conversation.isError ? (
            <ErrorState error={conversation.error} onRetry={() => void conversation.refetch()} />
          ) : (
            <ChatView conversationId={conversation.data.id} mode="ONBOARDING" />
          )}
        </section>

        <div className="mt-2 text-center">
          <Button variant="link" size="sm" onClick={() => skip.mutate()} loading={skip.isPending}>
            {t('setup.skip')}
          </Button>
        </div>
      </main>
    </div>
  );
}
